import { useEffect, useState } from 'react';

// Define types for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfig) => void;
          prompt: () => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonConfig) => void;
        };
      };
    };
  }
}

interface GoogleIdConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  use_fedcm_for_prompt?: boolean;
  use_fedcm_for_button?: boolean;
  context?: 'signin' | 'signup' | 'use';
  ux_mode?: 'popup' | 'redirect';
}

interface GoogleButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

interface GoogleCredentialResponse {
  credential: string; // JWT ID token
  select_by?: string;
}


export const useGoogleIdentity = () => {

  // TODO: we don't need isLoading or isLoaded
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if Google Identity Services is already loaded
    if (window.google?.accounts?.id) {
      setIsLoaded(true);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.error('Failed to load Google Identity Services');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // NOT WORKING WHEN USER CLICKED X
  const signInWithOneTap = async (clientId: string): Promise<string> => {
    if (!isLoaded || !window.google?.accounts?.id) {
      throw new Error('Google Identity Services not loaded');
    }

    setIsLoading(true);

    return new Promise((resolve, reject) => {
      try {
        // Initialize Google Identity Services for One Tap
        window.google?.accounts.id.initialize({
          client_id: clientId,
          callback: (response: GoogleCredentialResponse) => {
            setIsLoading(false);
            if (response.credential) {
              resolve(response.credential);
            } else {
              reject(new Error('No credential received from Google'));
            }
          },
          auto_select: true,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: true,
          context: 'signin', // Specify the context
          ux_mode: 'popup', // Use popup mode
        });

        // Display the One Tap UI
        let promptResolved = false;

        // // Monitor console errors for FedCM issues
        // const originalConsoleError = console.error;
        // const errorTimeout = setTimeout(() => {
        //   console.error = originalConsoleError;
        // }, 2000);

        // console.error = (...args: any[]) => {
        //   const message = args.join(' ');
        //   if (message.includes('FedCM was disabled') && !promptResolved) {
        //     promptResolved = true;
        //     clearTimeout(errorTimeout);
        //     console.error = originalConsoleError;
        //     setIsLoading(false);
        //     reject(new Error('FedCM disabled by user or browser settings'));
        //     return;
        //   }
        //   originalConsoleError.apply(console, args);
        // };

        window.google?.accounts.id.prompt(
          // @ts-ignore
          (notification: any) => {
            if (promptResolved) return;
            promptResolved = true;
            // clearTimeout(errorTimeout);
            // console.error = originalConsoleError;

            if (notification.isSkippedMoment()) {
              setIsLoading(false);
              const reason = notification.getSkippedReason?.() || 'unknown';
              console.error(`One Tap skipped: ${reason}`);
              reject(new Error(`One Tap skipped: ${reason}`));
            } else if (notification.isDismissedMoment?.()) {
              setIsLoading(false);
              const reason = notification.getDismissedReason?.() || 'unknown';
              console.error(`One Tap dismissed: ${reason}`);
              reject(new Error(`One Tap dismissed: ${reason}`));

            }
          }
        );
      } catch (error) {
        setIsLoading(false);
        console.error(`One Tap error: ${error}`);
        reject(error);
      }
    });
  };

  const renderSignInButton = (
    elementId: string,
    clientId: string,
    onSuccess: (credential: string) => void,
    onError?: (error: any) => void,
    buttonConfig?: GoogleButtonConfig
  ) => {
    if (!isLoaded || !window.google?.accounts?.id) {
      console.error('Google Identity Services not loaded');
      onError?.(new Error('Google Identity Services not loaded'));
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Element with ID ${elementId} not found`);
      onError?.(new Error(`Element with ID ${elementId} not found`));
      return;
    }

    // Initialize Google Identity Services
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: GoogleCredentialResponse) => {
        onSuccess(response.credential);
      },
      use_fedcm_for_button: true,
    });

    // Render the button
    window.google.accounts.id.renderButton(element, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'center',
      ...buttonConfig,
    });
  };

  return {
    isLoaded,
    isLoading,
    signInWithOneTap,
    renderSignInButton,
  };
};
